import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { SwapHoriz, Edit, Save, Cancel } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import type {
  AdminDialogProps,
  DroneWithOrganization,
  Organization,
  OrganizationsResponse,
  OwnershipType,
} from '../types/adminTools.types';

interface DroneAssignmentsDialogProps extends AdminDialogProps {
  dronesWithOrgs: DroneWithOrganization[];
  orgsData: OrganizationsResponse | null;
  loading: boolean;
  error: string;
  editingSerial: string | null;
  selectedOrg: string;
  updating: boolean;
  // Ownership editing props
  editingOwnershipSerial: string | null;
  selectedOwnershipType: OwnershipType | null;
  selectedLeaseEndDate: string;
  updatingOwnership: boolean;
  onStartEdit: (serial: string, currentOrgId: string | null) => void;
  onSaveOrg: (serial: string) => Promise<void>;
  onCancelEdit: () => void;
  onSelectOrg: (orgId: string) => void;
  // Ownership editing callbacks
  onStartOwnershipEdit: (
    serial: string,
    currentOwnershipType: OwnershipType | null,
    currentLeaseEndDate: string | null
  ) => void;
  onSaveOwnership: (serial: string) => Promise<void>;
  onCancelOwnershipEdit: () => void;
  onSelectOwnershipType: (type: OwnershipType | null) => void;
  onSelectLeaseEndDate: (date: string) => void;
}

export const DroneAssignmentsDialog: React.FC<DroneAssignmentsDialogProps> = ({
  open,
  onClose,
  dronesWithOrgs,
  orgsData,
  loading,
  error,
  editingSerial,
  selectedOrg,
  updating,
  editingOwnershipSerial,
  selectedOwnershipType,
  selectedLeaseEndDate,
  updatingOwnership,
  onStartEdit,
  onSaveOrg,
  onCancelEdit,
  onSelectOrg,
  onStartOwnershipEdit,
  onSaveOwnership,
  onCancelOwnershipEdit,
  onSelectOwnershipType,
  onSelectLeaseEndDate,
}) => {
  const { t } = useTranslation();

  // Check if a drone is assigned to a customer (non-XER org)
  const isCustomerAssigned = (drone: DroneWithOrganization): boolean => {
    return drone.organization_slug !== null && drone.organization_slug !== 'xer-technologies';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid="drone-assignments-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz />
          {t('adminToolsPage.droneAssignments.dialogTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : dronesWithOrgs ? (
          <Box sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('adminToolsPage.droneAssignments.tableHeaders.serialNumber')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.droneAssignments.tableHeaders.currentOrg')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.droneAssignments.tableHeaders.status')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.droneAssignments.tableHeaders.ownershipType')}
                    </TableCell>
                    <TableCell>
                      {t('adminToolsPage.droneAssignments.tableHeaders.leaseEndDate')}
                    </TableCell>
                    <TableCell align="center">
                      {t('adminToolsPage.droneAssignments.tableHeaders.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dronesWithOrgs.map((drone: DroneWithOrganization) => (
                    <TableRow key={drone.serial_number}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {drone.serial_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {editingSerial === drone.serial_number ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={selectedOrg}
                              onChange={(e) => onSelectOrg(e.target.value)}
                              disabled={updating}
                            >
                              {orgsData?.organizations.map((org: Organization) => (
                                <MenuItem key={org.id} value={org.id}>
                                  {org.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2">
                            {drone.organization_name ||
                              t('adminToolsPage.droneAssignments.noOrgAssigned')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={drone.status}
                          size="small"
                          color={
                            drone.status === 'Airworthy'
                              ? 'success'
                              : drone.status === 'Not Airworthy'
                                ? 'error'
                                : 'default'
                          }
                        />
                      </TableCell>
                      {/* Ownership Type Column */}
                      <TableCell>
                        {editingOwnershipSerial === drone.serial_number ? (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={selectedOwnershipType || ''}
                              onChange={(e) =>
                                onSelectOwnershipType(
                                  e.target.value === '' ? null : (e.target.value as OwnershipType)
                                )
                              }
                              disabled={updatingOwnership}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>{t('adminToolsPage.droneAssignments.noOwnership')}</em>
                              </MenuItem>
                              <MenuItem value="SOLD">SOLD</MenuItem>
                              <MenuItem value="LEASED">LEASED</MenuItem>
                            </Select>
                          </FormControl>
                        ) : isCustomerAssigned(drone) ? (
                          <Typography variant="body2">
                            {drone.ownership_type ||
                              t('adminToolsPage.droneAssignments.noOwnership')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      {/* Lease End Date Column */}
                      <TableCell>
                        {editingOwnershipSerial === drone.serial_number ? (
                          selectedOwnershipType === 'LEASED' ? (
                            <TextField
                              type="date"
                              size="small"
                              value={selectedLeaseEndDate}
                              onChange={(e) => onSelectLeaseEndDate(e.target.value)}
                              disabled={updatingOwnership}
                              InputLabelProps={{ shrink: true }}
                              sx={{ minWidth: 150 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )
                        ) : isCustomerAssigned(drone) && drone.ownership_type === 'LEASED' ? (
                          <Typography variant="body2">{drone.lease_end_date || '-'}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* Organization Edit Mode */}
                        {editingSerial === drone.serial_number ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onSaveOrg(drone.serial_number)}
                              disabled={updating}
                            >
                              {updating ? <CircularProgress size={16} /> : <Save />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={onCancelEdit}
                              disabled={updating}
                            >
                              <Cancel />
                            </IconButton>
                          </Box>
                        ) : editingOwnershipSerial === drone.serial_number ? (
                          /* Ownership Edit Mode */
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onSaveOwnership(drone.serial_number)}
                              disabled={updatingOwnership}
                            >
                              {updatingOwnership ? <CircularProgress size={16} /> : <Save />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={onCancelOwnershipEdit}
                              disabled={updatingOwnership}
                            >
                              <Cancel />
                            </IconButton>
                          </Box>
                        ) : (
                          /* Normal Mode - Show Edit Buttons */
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            {/* Edit Organization Button */}
                            <IconButton
                              size="small"
                              onClick={() =>
                                onStartEdit(drone.serial_number, drone.organization_id)
                              }
                              disabled={editingSerial !== null || editingOwnershipSerial !== null}
                              title="Edit Organization"
                            >
                              <Edit />
                            </IconButton>
                            {/* Edit Ownership Button - Only for customer-assigned drones */}
                            {isCustomerAssigned(drone) && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onStartOwnershipEdit(
                                    drone.serial_number,
                                    drone.ownership_type || null,
                                    drone.lease_end_date || null
                                  )
                                }
                                disabled={editingSerial !== null || editingOwnershipSerial !== null}
                                title="Edit Ownership"
                                sx={{ color: 'primary.main' }}
                              >
                                <SwapHoriz />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {dronesWithOrgs.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                {t('adminToolsPage.droneAssignments.noDronesFound')}
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
